import {
  CUISINE_TREE,
  DISH_FOLLOWUPS,
  DISH_TREE,
  resolveType,
  type CuisineNode,
} from './cuisine';
import {
  ALL_PRICE_LEVELS,
  DEFAULT_RADIUS_M,
  withFacts,
  type Answer,
  type Facts,
} from './facts';
import { typeLabel } from './mapping';
import { mealAt, mealCasualLabel, mealPlaceType, type Meal } from './meal';
import { DEFAULT_UNITS, formatRadius, type Units } from './units';

/**
 * Which parameter a question narrows. Only used for grouping and for the tests that
 * assert every filter-bearing axis gets resolved - the engine itself reads list order.
 */
export type Axis =
  'timing' | 'cuisine' | 'price' | 'distance' | 'style' | 'quality' | 'diet';

/**
 * Simple asks a short run and stops at cuisine level; advanced descends to dish level
 * and adds the dietary filter. Mode is chosen before a run starts, not answered during
 * one, so it shapes the question list rather than living in `Facts`.
 */
export type Mode = 'simple' | 'advanced';

export interface Question {
  id: string;
  axis: Axis;
  prompt: string;
  labels: { yes: string; no: string };
  /**
   * One line on what "Don't mind" does *here*. Skip closes an axis rather than
   * deferring a question, and what that means differs per axis - so the sentence
   * describing it lives on the same object as `apply.skip` rather than in the view,
   * where it would drift.
   */
  skipHint: string;
  /** Gate. When it returns false the question is skipped entirely - this is what makes
   *  dead states impossible, rather than relying on a hand-maintained jump table. */
  when?: (f: Facts) => boolean;
  apply: Record<Answer, (f: Facts) => Facts>;
}

/**
 * A built question list plus the caps that go with it. Bundled because the two travel
 * together everywhere: reading `questions` without the matching `maxQuestions` is how
 * a mode silently gets the wrong budget.
 */
export interface QuestionSet {
  mode: Mode;
  /**
   * The meal this set was built for. Exposed so the app can notice the clock has moved
   * past a mealtime boundary and rebuild, rather than offering "Just lunch" all evening.
   */
  meal: Meal;
  questions: Question[];
  maxQuestions: number;
  /** Denominator for the progress dots. The tree is adaptive, so this is an estimate. */
  estimatedQuestions: number;
}

/**
 * Per-mode budgets.
 *
 * `treeDepth` is a depth cap on a single descent, not a count of food questions: the
 * routing question sits outside it, and a dish-led run spends a few more on the tradition
 * follow-up. Timing, price and distance are asked *before* any of it, so they can no
 * longer be crowded out no matter how deep a tree gets. What the cap decides is where a
 * long descent stops.
 *
 *   simple    1 timing + 2 price + 2 distance + 7ish food + 2 tail          = 14
 *   advanced  the same, deeper refinements + the dietary filter             = 17
 *
 * Six is the floor for simple: the tradition tree holds 29 leaves, and five binary
 * questions can only tell 32 outcomes apart once the prevalence weighting has spent some
 * of that budget on shallow common answers - so a smaller cap would strand whole branches
 * at a regional type. Eight for advanced is what the deepest refinement needs
 * (South American -> Peruvian sits at depth eight).
 */
const LIMITS: Record<
  Mode,
  { maxQuestions: number; treeDepth: number; estimated: number }
> = {
  simple: { maxQuestions: 14, treeDepth: 6, estimated: 10 },
  advanced: { maxQuestions: 17, treeDepth: 8, estimated: 12 },
};

// ------------------------------------------------------------------ the food axes

/** The two descents. Each owns one path on `Facts` and one "closed" flag. */
type TreeAxis = 'cuisine' | 'dish';

const PATH_OF: Record<TreeAxis, (f: Facts) => string[]> = {
  cuisine: (f) => f.cuisinePath,
  dish: (f) => f.dishPath,
};

const IS_CLOSED: Record<TreeAxis, (f: Facts) => boolean> = {
  cuisine: (f) => f.cuisineAxisClosed,
  dish: (f) => f.dishAxisClosed,
};

const CLOSE: Record<TreeAxis, Partial<Facts>> = {
  cuisine: { cuisineAxisClosed: true },
  dish: { dishAxisClosed: true },
};

/**
 * Entering a node records it on that axis's path, then re-resolves the type.
 *
 * Re-resolving rather than assigning `child.type` is what makes the two axes interact:
 * a dish-led run that has just named a tradition may now match a `PAIRS` entry, and the
 * type it lands on is narrower than anything either path carries alone.
 */
function enter(axis: TreeAxis, child: CuisineNode) {
  return (f: Facts): Facts => {
    const path = [...PATH_OF[axis](f), child.id];
    const next = withFacts(
      f,
      axis === 'cuisine' ? { cuisinePath: path } : { dishPath: path },
    );
    return withFacts(next, { includedType: resolveType(next) });
  };
}

/**
 * What stopping the descent at this node actually gets you. The type is already
 * applied by the time the node's own question is asked, so this is a statement about
 * the search that would run, not a promise about the next answer.
 */
function cuisineSkipHint(type: string | undefined): string {
  const label = typeLabel(type);
  return label
    ? `Stops narrowing the food here - searches ${label}.`
    : 'Stops asking about food - searches anything nearby.';
}

/**
 * Turns a tree into flat questions, one per node that still has something to ask.
 *
 * The gate is exact path equality, so at most one question per tree is ever eligible and
 * the engine's "first unasked question whose gate passes" rule walks the descent for
 * free. `depth` truncates: a node at depth d asks the (d+1)th question of its descent, so
 * nodes at or past the cap simply never emit one.
 *
 * Parameterised on the axis because there are now three kinds of caller - the tradition
 * tree, the dish tree, and each dish's tradition follow-up - and they differ only in
 * which path they read and which extra gate they carry.
 */
function treeQuestions(
  root: CuisineNode,
  axis: TreeAxis,
  options: {
    depth: number;
    includeAdvanced: boolean;
    idPrefix: string;
    gate?: (f: Facts) => boolean;
  },
): Question[] {
  const pathOf = PATH_OF[axis];
  const isClosed = IS_CLOSED[axis];

  const build = (
    node: CuisineNode,
    path: string[],
    inherited?: string,
  ): Question[] => {
    // A node without a type of its own keeps its parent's, exactly as `resolveType` does.
    const type = node.type ?? inherited;
    const { branch } = node;
    if (!branch) return [];
    if (path.length >= options.depth) return [];
    if (branch.advanced && !options.includeAdvanced) return [];

    const here = path.join('/');
    const question: Question = {
      id: `${options.idPrefix}-${node.id}`,
      axis: 'cuisine',
      prompt: branch.prompt,
      labels: branch.labels,
      skipHint: cuisineSkipHint(type),
      when: (f) =>
        !isClosed(f) &&
        pathOf(f).join('/') === here &&
        (options.gate?.(f) ?? true),
      apply: {
        yes: enter(axis, branch.yes),
        no: enter(axis, branch.no),
        // Skip closes this axis and keeps whatever type it has already set - that is
        // what makes a third option worth having. The *other* axis stays open, so
        // "don't mind the cuisine, but I want noodles" is expressible.
        skip: (f) => withFacts(f, CLOSE[axis]),
      },
    };

    return [
      question,
      ...build(branch.yes, [...path, branch.yes.id], type),
      ...build(branch.no, [...path, branch.no.id], type),
    ];
  };

  return build(root, [], root.type);
}

/**
 * A short circuit, at the two hours where one is worth having.
 *
 * Breakfast and brunch places are their own category - people go *for brunch*, not for
 * brunch-flavoured Italian - so at those hours the cuisine trees are usually the wrong
 * question entirely. Answering yes settles the food axis outright and skips both
 * descents; answering no falls straight through to the routing question, having cost one
 * question to rule out the most likely answer.
 *
 * Only offered when the run said it is eating *right now*: "later on" deliberately means
 * every time of day, so there is no meal to short circuit on.
 *
 * Returns null for lunch and dinner, which have no Table A type and are not a kind of
 * place - "somewhere for lunch" is answered by any decent restaurant.
 */
function mealShortcutQuestion(meal: Meal): Question | null {
  const type = mealPlaceType(meal);
  if (!type) return null;

  return {
    id: 'meal-place',
    axis: 'cuisine',
    prompt: `A ${meal} place?`,
    labels: { yes: 'Yes', no: 'Something else' },
    // Skip and no do the same thing here, and saying so beats implying a difference.
    skipHint: 'Same as something else - carries on to the food questions.',
    when: (f) =>
      f.meal === meal && f.lead === undefined && !f.cuisineAxisClosed && !f.dishAxisClosed,
    apply: {
      yes: (f) =>
        withFacts(f, {
          includedType: type,
          // This *is* the food answer, so neither tree has anything left to ask.
          cuisineAxisClosed: true,
          dishAxisClosed: true,
        }),
      no: (f) => f,
      skip: (f) => f,
    },
  };
}

/**
 * The routing question, and the only one that decides which tree a run walks.
 *
 * It replaces "Eastern or Western food?" rather than adding to it. The old root cost the
 * same one question, carried no type, and asked people to place their appetite on a map;
 * this one asks something a hungry person can answer instantly and routes on the answer.
 */
const CUISINE_LEAD: Question = {
  id: 'cuisine-lead',
  axis: 'cuisine',
  prompt: 'A particular dish, or a kind of food?',
  labels: { yes: 'A dish', no: 'A kind of food' },
  skipHint: 'Stops asking about food - searches anything nearby.',
  when: (f) =>
    f.lead === undefined && !f.cuisineAxisClosed && !f.dishAxisClosed,
  apply: {
    yes: (f) => withFacts(f, { lead: 'dish' }),
    no: (f) => withFacts(f, { lead: 'cuisine' }),
    // Skipping the routing question closes both axes: there is no third tree to fall
    // through to, and leaving one open would put the user in a descent they declined.
    skip: (f) =>
      withFacts(f, { cuisineAxisClosed: true, dishAxisClosed: true }),
  },
};

/**
 * Every food question, in the order the engine should consider them.
 *
 * The dish follow-ups come last so a dish descent finishes before its tradition question
 * is offered: both are eligible only when their own path matches exactly, and the dish
 * tree's gate stops matching precisely when it reaches a leaf.
 */
function foodQuestions(options: {
  depth: number;
  includeAdvanced: boolean;
  meal: Meal;
}): Question[] {
  const followUps = Object.entries(DISH_FOLLOWUPS).flatMap(([dish, tree]) =>
    treeQuestions(tree, 'cuisine', {
      depth: options.depth,
      // Follow-ups are what make a dish-led run precise, so simple mode gets them too.
      includeAdvanced: true,
      idPrefix: 'follow',
      gate: (f) => f.dishPath[f.dishPath.length - 1] === dish,
    }),
  );

  const shortcut = mealShortcutQuestion(options.meal);

  return [
    // Ahead of the routing question: at breakfast it is the more likely answer, and a
    // "no" here costs one question to take the whole category off the table.
    ...(shortcut ? [shortcut] : []),
    CUISINE_LEAD,
    ...treeQuestions(CUISINE_TREE, 'cuisine', {
      depth: options.depth,
      includeAdvanced: options.includeAdvanced,
      idPrefix: 'cuisine',
      gate: (f) => f.lead === 'cuisine',
    }),
    ...treeQuestions(DISH_TREE, 'dish', {
      depth: options.depth,
      includeAdvanced: options.includeAdvanced,
      idPrefix: 'dish',
      gate: (f) => f.lead === 'dish',
    }),
    ...followUps,
  ];
}

// ------------------------------------------------------------------ the fixed axes

/**
 * The timing question now settles two things at once: whether to filter on opening
 * hours, and which meal the search is for.
 *
 * "Right now" is a much stronger statement than it was being treated as - it pins the
 * search to a specific time of day, and the app already knows what that is. "Later on"
 * deliberately leaves the meal unset, so the results cover every time of day rather than
 * whichever one the user happens to be standing in.
 */
function openNowQuestion(meal: Meal): Question {
  return {
    id: 'open-now',
    axis: 'timing',
    prompt: 'Eating right now?',
    labels: { yes: 'Right now', no: 'Later on' },
    skipHint: 'Ignores opening hours - closed places can still show up.',
    apply: {
      yes: (f) => withFacts(f, { openNow: true, meal }),
      no: (f) => withFacts(f, { openNow: false }),
      skip: (f) => f,
    },
  };
}

const PRICE_CHEAP: Question = {
  id: 'price-cheap',
  axis: 'price',
  prompt: 'Keeping it cheap?',
  labels: { yes: 'Cheap is good', no: 'Can spend a bit' },
  skipHint: 'Stops asking about price - any price level.',
  when: (f) => !f.priceLevels && !f.priceAxisClosed,
  apply: {
    yes: (f) =>
      withFacts(f, {
        priceLevels: ['PRICE_LEVEL_INEXPENSIVE', 'PRICE_LEVEL_MODERATE'],
      }),
    no: (f) => f,
    skip: (f) => withFacts(f, { priceAxisClosed: true }),
  },
};

/**
 * The everyday half is named for the meal in front of you rather than always "dinner",
 * which read as a small lie at half past eight in the morning. The meal comes from the
 * clock, not from the timing answer, so the label is right even before that question is
 * reached - and it stays right whichever way it gets answered.
 */
function priceFancyQuestion(meal: Meal): Question {
  return {
    id: 'price-fancy',
    axis: 'price',
    prompt: 'Special occasion?',
    labels: { yes: 'Treat ourselves', no: mealCasualLabel(meal) },
    skipHint: 'No price filter at all - cheap and upscale both count.',
    // Only reachable when "keeping it cheap?" was answered "no".
    when: (f) => !f.priceLevels && !f.priceAxisClosed,
    apply: {
      yes: (f) =>
        withFacts(f, {
          priceLevels: ['PRICE_LEVEL_EXPENSIVE', 'PRICE_LEVEL_VERY_EXPENSIVE'],
        }),
      no: (f) =>
        withFacts(f, {
          priceLevels: ['PRICE_LEVEL_MODERATE', 'PRICE_LEVEL_EXPENSIVE'],
        }),
      skip: (f) =>
        withFacts(f, { priceAxisClosed: true, priceLevels: ALL_PRICE_LEVELS }),
    },
  };
}

/**
 * The distance ladder. None of this comes from Google - its only say is that the bias
 * circle caps at 50 km, and even inside that the circle is a hint rather than a filter
 * (rank.ts enforces the real radius by haversine). So these are travel times, straight
 * line, with `RADIUS_SLACK` and real street routing both stretching them further:
 *
 *   1.2 km   ~15 minutes on foot
 *   5 km     ~10 minutes driving through town
 *   16 km    ~20-25 minutes driving, which is what "worth a drive" has to mean
 *
 * Skipping either question lands on `DEFAULT_RADIUS_M`, the same radius as never being
 * asked at all: "don't mind" and "never came up" are the same statement about distance,
 * and having them disagree - 5 km one way, 8 km the other - was a difference nothing
 * could justify.
 *
 * These two are also the only questions whose hint quotes a number, which is why they
 * are built per-run: the radius stays in metres either way and the hint says it in
 * whichever units the user is reading. Each hint quotes the same constant its own branch
 * applies, so the sentence cannot drift from the search.
 */
const WALK_RADIUS_M = 1200;
const LOCAL_RADIUS_M = 5000;
const DRIVE_RADIUS_M = 16_000;

function distanceWalk(units: Units): Question {
  return {
    id: 'distance-walk',
    axis: 'distance',
    prompt: 'Walking distance?',
    labels: { yes: 'On foot', no: 'Happy to travel' },
    skipHint: `Stops asking about distance - searches about ${formatRadius(
      DEFAULT_RADIUS_M,
      units,
    )} around you.`,
    when: (f) => f.radiusM === undefined && !f.distanceAxisClosed,
    apply: {
      yes: (f) => withFacts(f, { radiusM: WALK_RADIUS_M }),
      no: (f) => f,
      skip: (f) =>
        withFacts(f, { distanceAxisClosed: true, radiusM: DEFAULT_RADIUS_M }),
    },
  };
}

function distanceDrive(units: Units): Question {
  return {
    id: 'distance-drive',
    axis: 'distance',
    prompt: 'Worth a proper drive?',
    labels: { yes: "I'll drive", no: 'Keep it local' },
    skipHint: `Settles the distance - searches about ${formatRadius(
      DEFAULT_RADIUS_M,
      units,
    )} around you.`,
    when: (f) => f.radiusM === undefined && !f.distanceAxisClosed,
    apply: {
      yes: (f) => withFacts(f, { radiusM: DRIVE_RADIUS_M }),
      no: (f) => withFacts(f, { radiusM: LOCAL_RADIUS_M }),
      skip: (f) =>
        withFacts(f, { distanceAxisClosed: true, radiusM: DEFAULT_RADIUS_M }),
    },
  };
}

const SIT_DOWN: Question = {
  id: 'sit-down',
  axis: 'style',
  prompt: 'Sit down and take your time?',
  labels: { yes: 'Sit down', no: 'Grab and go' },
  skipHint: 'No preference - sit-down and takeaway both count.',
  apply: {
    yes: (f) => withFacts(f, { sitDown: true }),
    no: (f) =>
      withFacts(f, {
        sitDown: false,
        // Only claim the type if the descent left it open - a cuisine always wins.
        includedType: f.includedType ?? 'fast_food_restaurant',
      }),
    skip: (f) => f,
  },
};

/**
 * Google rounds `minRating` up to the nearest 0.5 and drops unrated places entirely,
 * so this is opt-in: on a narrow cuisine in a thin area it can empty the results. The
 * Bayesian term in rank.ts already prefers well-reviewed places softly; this is for
 * when the user wants it as a hard filter.
 */
const WELL_REVIEWED: Question = {
  id: 'well-reviewed',
  axis: 'quality',
  prompt: 'Only well-reviewed places?',
  labels: { yes: 'Only the good stuff', no: "I'll take my chances" },
  // Skip and no do the same thing here, and saying so beats implying a difference.
  skipHint: 'Same as taking your chances - no rating filter.',
  apply: {
    yes: (f) => withFacts(f, { minRating: 4 }),
    no: (f) => f,
    skip: (f) => f,
  },
};

/**
 * Advanced only, and deliberately steers the text query instead of setting
 * `includedType: vegetarian_restaurant`: the ask is "somewhere I can eat well", which
 * a good Thai place answers as well as a dedicated vegetarian one. Forcing the type
 * would also collide with whatever the descent chose.
 */
const VEGETARIAN: Question = {
  id: 'vegetarian',
  axis: 'diet',
  prompt: 'Need solid vegetarian options?',
  labels: { yes: 'Yes', no: 'Not fussed' },
  skipHint: 'Same as not fussed - nothing added to the search.',
  apply: {
    yes: (f) => withFacts(f, { vegetarian: true }),
    no: (f) => f,
    skip: (f) => f,
  },
};

/**
 * Order matters, and differently than it used to.
 *
 * The axes that must always be asked - timing, price, distance - run *first*, so the
 * guarantee holds by construction rather than by arithmetic. (It used to depend on the
 * cuisine ladder being capped at three rejections, which a deeper tree would have quietly
 * broken - and the food axis is now two trees plus a follow-up, so it is deeper again.)
 * The soft refinements sit at the tail, where the question cap trims them if a long
 * descent uses up the budget.
 */
export function buildQuestions(
  mode: Mode = 'simple',
  units: Units = DEFAULT_UNITS,
  now: Date = new Date(),
): QuestionSet {
  const limits = LIMITS[mode];
  const meal = mealAt(now);

  const questions: Question[] = [
    openNowQuestion(meal),
    PRICE_CHEAP,
    priceFancyQuestion(meal),
    distanceWalk(units),
    distanceDrive(units),
    ...foodQuestions({
      depth: limits.treeDepth,
      includeAdvanced: mode === 'advanced',
      meal,
    }),
    SIT_DOWN,
    WELL_REVIEWED,
    ...(mode === 'advanced' ? [VEGETARIAN] : []),
  ];

  return {
    mode,
    meal,
    questions,
    // The shortcut is one more question a run can be asked before the descent even
    // starts, so the budget has to carry it rather than silently trimming the tail.
    maxQuestions: limits.maxQuestions + (mealPlaceType(meal) ? 1 : 0),
    estimatedQuestions: limits.estimated,
  };
}
